import { Box, Text, Flex } from "@chakra-ui/react";
import { useTranslation, Trans } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { InvoiceFormData } from "@/types";
import { formatCostText } from "@/utils/invoiceUtils";
import { Heading, Alert, BodyShort } from "@digital-services-gard-as/anchor-react";

type Props = {
    totalAmountForm: number;
    totalAmountOcr?: number;
    ocrCurrency?: string;
};

export const TotalAmount = ({ totalAmountForm, totalAmountOcr, ocrCurrency }: Props) => {
    const { t } = useTranslation("invoices");
    const { watch } = useFormContext<InvoiceFormData>();
    const currency = watch("bankAccount.currency");

    const formattedFormTotal = formatCostText(totalAmountForm, currency);
    const formattedOcrTotal = totalAmountOcr ? formatCostText(totalAmountOcr, ocrCurrency) : undefined;

    const hasCurrencyMismatch = currency && ocrCurrency && currency !== ocrCurrency;
    const hasTotalAmountMismatch =
        Boolean(totalAmountOcr) &&
        Boolean(totalAmountForm) &&
        totalAmountForm !== totalAmountOcr &&
        !hasCurrencyMismatch;

    return (
        <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
            <Heading size="md" style={{ marginBottom: "0.5rem" }}>
                {t("invoices:createInvoice.feesAndExpenses.totalFeesExpensesTaxes")}
            </Heading>
            <Text mb={2}>{t("invoices:createInvoice.feesAndExpenses.totalFeesExpensesTaxesHelp")}</Text>
            {hasCurrencyMismatch && (
                <Alert
                    variant="danger"
                    contentMaxWidth={false}
                    style={{ marginBottom: "1rem" }}
                    data-testid="currencyMismatchAlert"
                >
                    <BodyShort>
                        <Trans
                            i18nKey="invoices:createInvoice.feesAndExpenses.currencyMismatchWarning"
                            values={{
                                bankAccountCurrency: currency,
                                ocrCurrency
                            }}
                        />
                    </BodyShort>
                </Alert>
            )}
            {hasTotalAmountMismatch && (
                <Alert
                    variant="danger"
                    contentMaxWidth={false}
                    style={{ marginBottom: "1rem" }}
                    data-testid="totalAmountMismatchAlert"
                >
                    <BodyShort>
                        <Trans
                            i18nKey="invoices:createInvoice.feesAndExpenses.totalAmountMismatchWarning"
                            values={{
                                totalAmountOcr: formattedOcrTotal
                            }}
                        />
                    </BodyShort>
                </Alert>
            )}

            <Flex gap={6} flexDirection="row">
                <AmountBox
                    amount={formattedFormTotal}
                    label={t("invoices:createInvoice.feesAndExpenses.enteredTotalAmount")}
                />
            </Flex>
        </Box>
    );
};

type AmountBoxProps = {
    amount: string;
    label?: string;
};

const AmountBox = ({ amount, label }: AmountBoxProps) => {
    return (
        <Flex bg="#EEECEA" p={4} maxW="20rem" w="100%" flexDirection="column" justifyContent="center">
            {label && (
                <Text mb={2} fontWeight="bold">
                    {label}
                </Text>
            )}
            <Flex direction="column" gap={2}>
                <Text fontSize="2xl" data-testid="totalAmount" asChild>
                    <b>{amount}</b>
                </Text>
            </Flex>
        </Flex>
    );
};
